import { useEffect } from "react";
import { FaCheck } from "react-icons/fa";
import { useListarUsuariosPorEquipe } from "services/api/Identity_Usuario/listar-usuarios-por-equipe";
import styled from "styled-components";

interface Props {
  cargos: {
    isCorretor: boolean;
    isGerenteVendas: boolean;
    isSuperintendente: boolean;
  };
  corretoresSelecionados: string[];
  setCorretoresSelecionados: (corretores: string[]) => void;
  corretoresData: string[];
  idEquipe: string;
  nomeEquipe: string;
}

const CorretoresSelector: React.FC<Props> = ({
  cargos,
  corretoresSelecionados,
  setCorretoresSelecionados,
  corretoresData,
  idEquipe,
  nomeEquipe,
}) => {
  const { data: dataCorretores, refetch: refetchCorretores } =
    useListarUsuariosPorEquipe({
      queryKey: "listar-usuarios-por-equipe",
      IdUsuarioLider: [idEquipe ?? ""],
    });

  useEffect(() => {
    if (idEquipe) {
      refetchCorretores();
    }
  }, [idEquipe]);

  const todosCorretores = dataCorretores?.data || [];

  // Se for superintendente, mostra apenas os corretores selecionados
  // Se for gerente, mostra todos os corretores
  const corretores = cargos.isSuperintendente
    ? todosCorretores.filter((corretor: any) =>
        corretoresData?.includes(corretor.idCorretor)
      )
    : todosCorretores;

  // Compara os corretores da listagem com os do detalhe e marca os selecionados (apenas para gerente)
  useEffect(() => {
    if (
      cargos.isGerenteVendas &&
      corretoresData &&
      corretoresData.length > 0 &&
      todosCorretores.length > 0
    ) {
      const corretoresSelecionadosDoDetalhe = todosCorretores
        .filter((corretor: any) => corretoresData.includes(corretor.idCorretor))
        .map((corretor: any) => corretor.idCorretor);

      setCorretoresSelecionados(corretoresSelecionadosDoDetalhe);
    } else if (cargos.isSuperintendente && corretoresData) {
      // Para superintendente, apenas seta os IDs dos corretores selecionados
      setCorretoresSelecionados(corretoresData);
    }
  }, [
    corretoresData,
    todosCorretores,
    cargos.isGerenteVendas,
    cargos.isSuperintendente,
  ]);

  const toggleCorretor = (id: string) => {
    const novosCorretores = corretoresSelecionados?.includes(id)
      ? corretoresSelecionados?.filter((item) => item !== id)
      : [...corretoresSelecionados, id];
    setCorretoresSelecionados(novosCorretores);
  };

  const toggleTodos = () => {
    if (corretoresSelecionados?.length === corretores?.length) {
      setCorretoresSelecionados([]);
    } else {
      setCorretoresSelecionados(corretores.map((c: any) => c.idCorretor));
    }
  };

  const limparSelecao = () => {
    setCorretoresSelecionados([]);
  };

  const allSelected = corretoresSelecionados?.length === corretores?.length;

  return (
    <Container>
      <Header>
        <TextStrong>Equipe: </TextStrong>
        <EquipeText>{nomeEquipe}</EquipeText>
      </Header>

      <CorretoresHeader>
        <TextStrong>Corretores</TextStrong>
        <ContainerRightList>
          <TextStrong>
            {corretoresSelecionados?.length} selecionado
            {corretoresSelecionados?.length !== 1 ? "s" : ""}
          </TextStrong>
          {cargos.isGerenteVendas && (
            <LimparSelecaoLink onClick={limparSelecao}>
              Limpar seleção
            </LimparSelecaoLink>
          )}
        </ContainerRightList>
      </CorretoresHeader>

      <CorretorList>
        {cargos.isGerenteVendas && (
          <Option onClick={toggleTodos}>
            <Checkbox isChecked={allSelected}>
              {allSelected && <CheckIcon />}
            </Checkbox>
            <ListTitle>Todos</ListTitle>
          </Option>
        )}

        {corretores.map((corretor: any) => {
          const isSelected = corretoresSelecionados.includes(
            corretor.idCorretor
          );
          return (
            <Option
              key={corretor.idCorretor}
              onClick={
                cargos.isGerenteVendas
                  ? () => toggleCorretor(corretor.idCorretor)
                  : undefined
              }
            >
              {cargos.isGerenteVendas && (
                <Checkbox isChecked={isSelected}>
                  {isSelected && <CheckIcon />}
                </Checkbox>
              )}
              <ListTitle>{corretor.apelido}</ListTitle>
            </Option>
          );
        })}
      </CorretorList>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const Header = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const EquipeText = styled.p`
  font-family: Inter;
  font-weight: 500;
  font-style: Medium;
  font-size: 16px;
  line-height: 150%;
  letter-spacing: 0%;
  color: #424248;
`;

export const TextStrong = styled.h1`
  font-family: Inter;
  font-weight: 600;
  font-style: Semi Bold;
  font-size: 16px;
  line-height: 130%;
  letter-spacing: 0%;
  color: #424248;
`;

const CorretoresHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  justify-content: space-between;
`;

const ListTitle = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
  color: #4a4a4a;
`;

const LimparSelecaoLink = styled.button`
  background: none;
  border: none;
  color: #4361ee;
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 150%;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  margin-left: auto;
`;

const CorretorList = styled.div`
  background-color: white;
  border: 1px solid #d5d5d5;
  padding: 16px 0;
  border-radius: 8px;
  overflow: hidden;
  max-height: 350px;
  overflow-y: auto;
`;

const ContainerRightList = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 16px;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f9f9f9;
  }
`;

const Checkbox = styled.div<{ isChecked: boolean }>`
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #d5d5d5;
  border-radius: 4px;
  margin-right: 12px;
  background-color: ${({ isChecked }) =>
    isChecked ? "#4361ee" : "transparent"};
  border-color: ${({ isChecked }) => (isChecked ? "#4361ee" : "#d5d5d5")};

  svg {
    width: 12px;
    height: 12px;
    color: white;
  }
`;

const CheckIcon = styled(FaCheck)`
  width: 12px;
  height: 12px;
  color: white;
`;

export default CorretoresSelector;
